import React, { useEffect, useState } from 'react';
import { Camera, CheckCircle, Edit2, ExternalLink, MapPin, UserPlus, X, XCircle } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import ImageLightbox from '../ImageLightbox';
import { authedMediaUrl } from '../../utils/media';
import { formatClinicDate, formatClinicDateTime, formatClinicTime } from '../../utils/clinicTime';
import { CorrectEntryModal } from './EntryEditModals';
import { REVIEW_LABEL, describePosition, formatHours } from './attendanceStatus';

// One attendance entry in full: both selfies, where each punch was made, the times, the review
// state and the correction history. Reviewing a flagged punch and correcting the times happen here.

const time = (value) => formatClinicTime(value, { hour: 'numeric', minute: '2-digit', hour12: true }, '');

const REVIEW_CHIP = {
  NONE: 'bg-gray-100 text-gray-600',
  FLAGGED: 'bg-amber-100 text-amber-800',
  ACCEPTED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-red-100 text-red-800',
};

const Punch = ({ label, at, side, onZoom }) => {
  const photo = side?.photoUrl ? authedMediaUrl(side.photoUrl) : null;
  return (
    <div className="rounded-xl border border-gray-200 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
        <span className="font-semibold text-gray-900">{time(at) || '–'}</span>
      </div>
      {photo ? (
        <button type="button" onClick={() => onZoom(photo, `${label} selfie`)} className="block w-full rounded-lg overflow-hidden bg-gray-100 aspect-square">
          <img src={photo} alt={`${label} selfie`} className="w-full h-full object-cover" />
        </button>
      ) : (
        <div className="rounded-lg bg-gray-50 aspect-square flex flex-col items-center justify-center text-gray-400">
          <Camera className="w-6 h-6 mb-1" aria-hidden="true" />
          <span className="text-xs text-center px-2">
            {!at
              ? 'Not punched'
              : side?.photoDeleted
                // Deleted on schedule, not missing: the punch had a selfie and it was kept as long
                // as the attendance settings say.
                ? `Photo deleted after ${side.photoKeptDays || 'the kept number of'} days`
                : 'No photo'}
          </span>
        </div>
      )}
      {side?.markedByName && (
        <p className="text-xs text-gray-600 flex items-start gap-1">
          <UserPlus className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-400" aria-hidden="true" />
          <span>
            Marked by <span className="text-gray-900">{side.markedByName}</span>
            {side.markedReason && <span className="block text-gray-500">{side.markedReason}</span>}
          </span>
        </p>
      )}
      {side && at && (() => {
        const where = describePosition(side);
        const tone = where.tone === 'ok' ? 'text-emerald-700' : where.tone === 'warn' ? 'text-amber-700' : 'text-gray-600';
        return (
          <p className={`text-xs flex items-start gap-1 ${tone}`}>
            <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              {where.verdict}
              {/* The coordinates themselves, because "not checked" on its own tells a reviewer
                  nothing about where the person actually was. */}
              {where.coords && (
                <a
                  href={where.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block font-mono text-[11px] text-navy-700 hover:underline"
                >
                  {where.coords}
                  <ExternalLink className="w-3 h-3 inline ml-1 -mt-0.5" aria-hidden="true" />
                </a>
              )}
              {where.accuracy && <span className="block text-gray-400">{where.accuracy}</span>}
              {where.hint && <span className="block text-gray-500">{where.hint}</span>}
            </span>
          </p>
        );
      })()}
    </div>
  );
};

/**
 * Why a flagged punch is waiting, in the same terms as the day's chip: away on a real position, on
 * an approximate location only (which places nobody), or with no position at all.
 */
const flaggedBecause = (entry) => {
  const sides = [entry.in, entry.out].filter(Boolean);
  if (sides.some((s) => s.withinRange === false && !s.approximate)) return "This punch was made outside the hospital's range.";
  if (sides.some((s) => s.approximate)) return "The phone shared only an approximate location, so this punch cannot show whether it was made at the hospital.";
  return 'This punch was made without a position.';
};

const EntryDetailModal = ({ entry: initial, canReview, canCorrect, onClose, onChanged }) => {
  const notify = useToast();
  const [entry, setEntry] = useState(initial);
  const [history, setHistory] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [correcting, setCorrecting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialog(() => { if (!saving && !zoom && !correcting) onClose(); }, { confirmDiscard: false });

  useEffect(() => {
    let live = true;
    attendanceAPI.corrections(entry.id)
      .then((rows) => { if (live) setHistory(Array.isArray(rows) ? rows : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [entry.id, entry.corrected]);

  const review = async (decision) => {
    if (decision === 'REJECTED' && !note.trim()) { setError('Say why the punch is rejected.'); return; }
    setSaving(true);
    setError('');
    try {
      const saved = await attendanceAPI.review(entry.id, decision, note.trim() || null);
      setEntry(saved);
      setRejecting(false);
      notify('success', decision === 'ACCEPTED' ? 'Punch accepted' : 'Punch rejected');
      onChanged(saved);
    } catch (e) {
      setError(e?.message || 'The review could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const flagged = entry.reviewStatus === 'FLAGGED';
  const flaggedFor = flaggedBecause(entry);

  return (
    <>
      <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="entry-title">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col">
          <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
            <div className="min-w-0">
              <h2 id="entry-title" className="text-lg font-bold text-white truncate">{entry.userName}</h2>
              <p className="text-sm text-navy-100">{formatClinicDate(entry.workDate, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </div>
            <button type="button" aria-label="Close" onClick={onClose} disabled={saving} className="p-2 hover:bg-white/20 rounded-lg">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-gray-900">{formatHours(entry.hoursWorked)}</span>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${REVIEW_CHIP[entry.reviewStatus] || REVIEW_CHIP.NONE}`}>
                {REVIEW_LABEL[entry.reviewStatus] || entry.reviewStatus}
              </span>
              {entry.status === 'OPEN' && <span className="inline-flex rounded-full bg-navy-100 text-navy-800 px-2.5 py-0.5 text-xs font-semibold">Still in</span>}
              {entry.status === 'AUTO_CLOSED' && <span className="inline-flex rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-xs font-semibold">Closed by the system</span>}
              {entry.manual && <span className="inline-flex rounded-full bg-gray-100 text-gray-700 px-2.5 py-0.5 text-xs font-semibold">Entered by hand</span>}
              {entry.corrected && !entry.manual && <span className="inline-flex rounded-full bg-gray-100 text-gray-700 px-2.5 py-0.5 text-xs font-semibold">Corrected</span>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Punch label="In" at={entry.clockInAt} side={entry.in} onZoom={(src, alt) => setZoom({ src, alt })} />
              <Punch label="Out" at={entry.clockOutAt} side={entry.out} onZoom={(src, alt) => setZoom({ src, alt })} />
            </div>

            {entry.reviewedByName && (
              <p className="text-sm text-gray-600">
                {REVIEW_LABEL[entry.reviewStatus]} by {entry.reviewedByName} on {formatClinicDateTime(entry.reviewedAt)}
                {entry.reviewNote && <span className="block text-gray-800 mt-0.5">“{entry.reviewNote}”</span>}
              </p>
            )}

            {flagged && canReview && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-3">
                <p className="text-sm text-amber-900 font-medium">{flaggedFor} Does it count?</p>
                {rejecting && (
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    autoFocus
                    aria-label="Reason for rejecting"
                    placeholder="Why the punch is rejected"
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  />
                )}
                {error && <div className="text-sm text-red-700">{error}</div>}
                <div className="flex gap-2">
                  {rejecting ? (
                    <>
                      <button type="button" onClick={() => { setRejecting(false); setError(''); }} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">Back</button>
                      <button type="button" onClick={() => review('REJECTED')} disabled={saving} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1.5">
                        <XCircle className="w-4 h-4" aria-hidden="true" /> Reject
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => setRejecting(true)} disabled={saving} className="flex-1 px-4 py-2 border border-red-300 bg-white text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 flex items-center justify-center gap-1.5">
                        <XCircle className="w-4 h-4" aria-hidden="true" /> Reject
                      </button>
                      <button type="button" onClick={() => review('ACCEPTED')} disabled={saving} className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1.5">
                        <CheckCircle className="w-4 h-4" aria-hidden="true" /> Accept
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {history.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Corrections</h3>
                <ul className="space-y-1.5">
                  {history.map((c) => (
                    <li key={c.id} className="text-xs text-gray-600 rounded-lg bg-gray-50 px-3 py-2">
                      <span className="text-gray-900 font-medium">{c.changedByName}</span>, {formatClinicDateTime(c.changedAt)}:
                      {' '}{time(c.beforeClockInAt) || '–'}–{time(c.beforeClockOutAt) || '–'} to {time(c.afterClockInAt)}–{time(c.afterClockOutAt)}
                      <span className="block text-gray-800">“{c.reason}”</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t bg-gray-50 flex justify-between gap-2 rounded-b-2xl">
            {canCorrect && entry.status !== 'OPEN' ? (
              <button type="button" onClick={() => setCorrecting(true)} disabled={saving} className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-1.5">
                <Edit2 className="w-4 h-4" aria-hidden="true" /> Correct times
              </button>
            ) : <span />}
            <button type="button" onClick={onClose} disabled={saving} className="px-5 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Done</button>
          </div>
        </div>
      </div>

      {zoom && <ImageLightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />}
      {correcting && (
        <CorrectEntryModal
          entry={entry}
          onClose={() => setCorrecting(false)}
          onSaved={(saved) => { setEntry(saved); setCorrecting(false); onChanged(saved); }}
        />
      )}
    </>
  );
};

export default EntryDetailModal;
